import { useEffect, useState } from 'react'
import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { User } from '../api/client'
import { AppShell } from '../components/app-shell'
import { Avatar } from '../components/avatar'
import { BrowseChannelsDialog, CreateChannelDialog } from '../components/channel-dialogs'
import { versionQuery } from '../lib/api'
import { isDirect, myChannelsQuery } from '../lib/channels'
import { usersQuery } from '../lib/users'
import { myThreadsQuery } from '../lib/threads'
import { usePresence } from '../lib/presence'
import { NewDMDialog } from '../components/dm-dialog'
import { CommandPalette, useCommandPalette } from '../components/command-palette'
import { TooltipProvider } from '../components/ui/tooltip'
import { Bell, Bookmark, LogOut, MessagesSquare, Search, Settings, Users, Command as CommandIcon } from 'lucide-react'
import { ConnectionStatus, RealtimeProvider } from '../lib/realtime-context'
import { useTitleBadge } from '../lib/unreads'
import { ChannelSections } from '../components/sidebar-sections'
import { useCustomEmojiSync } from '../lib/custom-emoji'
import { SavedSearchesNav } from '../components/saved-searches-nav'

// Pathless layout route: everything under it requires a session.
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const me = await context.auth.me()
    if (!me) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    return { me }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(myChannelsQuery),
  component: AppLayout,
})

function AppLayout() {
  const { auth, me } = Route.useRouteContext()
  const navigate = useNavigate()
  useCustomEmojiSync()

  return (
    <RealtimeProvider>
      <TooltipProvider>
      <AppShell
        sidebar={
          <Sidebar
            me={me}
            onLogout={async () => {
              await auth.logout()
              await navigate({ to: '/login' })
            }}
          />
        }
      >
        <Outlet />
      </AppShell>
      </TooltipProvider>
    </RealtimeProvider>
  )
}

function Sidebar({ me, onLogout }: { me: User; onLogout: () => void }) {
  const navigate = useNavigate()
  const version = useQuery(versionQuery)
  const channels = useQuery(myChannelsQuery)
  useTitleBadge()
  const [dialog, setDialog] = useState<'create' | 'browse' | 'dm' | null>(null)
  useEffect(() => {
    const open = () => setDialog('create')
    document.addEventListener('dispatch:create-channel', open)
    return () => document.removeEventListener('dispatch:create-channel', open)
  }, [])
  const palette = useCommandPalette()
  const [renderedAt] = useState(() => Date.now())
  const canCreate = me.role !== 'guest'
  const users = useQuery(usersQuery)
  const threads = useQuery(myThreadsQuery)
  const threadUnread = (threads.data ?? []).reduce((n, t) => n + (t.unread > 0 ? 1 : 0), 0)
  const dms = (channels.data ?? []).filter(isDirect)
  const dmPeers = dms.flatMap((c) => (c.member_ids ?? []).filter((id) => id !== me.id))
  const presence = usePresence(dmPeers)

  return (
    <>
      <div className="flex h-11 items-center justify-between px-3">
        <span className="truncate text-[13px] font-semibold">Dispatch</span>
        <button type="button" onClick={() => palette.setOpen(true)} className="flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Open command palette" title="Command palette">
          <CommandIcon size={11} />K
        </button>
      </div>
      <form
        className="px-2 pb-1"
        onSubmit={(e) => {
          e.preventDefault()
          const q = String(new FormData(e.currentTarget).get('q') ?? '').trim()
          void navigate({ to: '/search', search: q ? { q } : {} })
          e.currentTarget.reset()
        }}
      >
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <input name="q" type="search" placeholder="Search" aria-label="Search" className="h-7 w-full rounded-md border border-border bg-bg pl-7 pr-2 text-[13px] outline-none placeholder:text-fg-subtle hover:border-border-strong focus:border-accent" />
        </div>
      </form>
      <nav className="flex-1 overflow-y-auto p-2">
        <Link to="/threads" className="flex items-center justify-between gap-2 rounded-md px-2 py-[3px] text-[13px] text-fg-muted hover:bg-bg-hover hover:text-fg" activeProps={{ className: 'bg-bg-hover text-fg font-medium' }} data-testid="threads-link">
          <span className="flex items-center gap-2"><MessagesSquare size={14} /> Threads</span>
          {threadUnread > 0 && <span className="rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-fg">{threadUnread}</span>}
        </Link>
        <Link to="/saved" className="flex items-center gap-2 rounded-md px-2 py-[3px] text-[13px] text-fg-muted hover:bg-bg-hover hover:text-fg" activeProps={{ className: 'bg-bg-hover text-fg font-medium' }}>
          <Bookmark size={14} /> Saved
        </Link>
        <SavedSearchesNav />
        {canCreate && <Link to="/people" className="mb-3 flex items-center gap-2 rounded-md px-2 py-[3px] text-[13px] text-fg-muted hover:bg-bg-hover hover:text-fg" activeProps={{ className: 'bg-bg-hover text-fg font-medium' }}>
          <Users size={14} /> People
        </Link>}
        <ChannelSections
          channels={channels.data ?? []}
          now={renderedAt}
          canCreate={canCreate}
          onBrowse={() => setDialog('browse')}
          onCreate={() => setDialog('create')}
          onNewDM={() => setDialog('dm')}
          dm={{ me, users: users.data ?? [], presence }}
        />
      </nav>
      <div className="space-y-1 border-t border-border px-2 py-2 text-xs">
        <div className="flex items-center justify-between gap-1">
          <Link to="/settings/profile" className="flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 hover:bg-bg-hover" activeProps={{ className: 'text-fg' }}>
            <Avatar user={me} size={22} />
            <span className="truncate text-[13px]">
              <span className="font-medium">{me.display_name}</span> <span className="text-fg-muted">@{me.handle}</span>
            </span>
          </Link>
          <span className="flex shrink-0 items-center gap-0.5">
            <Link to="/settings/notifications" className="rounded-md p-1 text-fg-muted hover:bg-bg-hover hover:text-fg" activeProps={{ className: 'text-fg' }} title="Notification preferences" aria-label="Notification preferences">
              <Bell size={14} />
            </Link>
            <Link to="/settings/sessions" className="rounded-md p-1 text-fg-muted hover:bg-bg-hover hover:text-fg" activeProps={{ className: 'text-fg' }} title="Settings" aria-label="Settings">
              <Settings size={14} />
            </Link>
            <button type="button" onClick={onLogout} className="rounded-md p-1 text-fg-muted hover:bg-bg-hover hover:text-fg" title="Log out" aria-label="Log out">
              <LogOut size={14} />
            </button>
          </span>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-2 overflow-hidden px-1 font-mono text-[11px] text-fg-subtle">
          <span className="truncate" title={version.data ? `server ${version.data.version}` : undefined}>
            {version.data ? `server ${version.data.version.slice(0, 7)}` : version.isError ? 'server unreachable' : '…'}
          </span>
          <ConnectionStatus />
        </div>
      </div>
      <CreateChannelDialog open={dialog === 'create'} onClose={() => setDialog(null)} />
      <BrowseChannelsDialog open={dialog === 'browse'} onClose={() => setDialog(null)} />
      <NewDMDialog open={dialog === 'dm'} onClose={() => setDialog(null)} me={me} />
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} me={me} onAction={(a) => setDialog(a)} />
    </>
  )
}
